import { CheckCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import useOrgAccess from '_/hooks/useOrgAccess';
import { useSchemas } from '_/hooks/useSchemas';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Table, Space, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { ReactElement, useState, useEffect, useCallback } from 'react';

interface Schema {
  id: string;
  name: string;
  slug: string;
  createdOn: string;
  published: boolean;
}

function OrganizationSchemas(): ReactElement {
  const history = useNavigate();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const { data: schemas = [], refetch, isRefetching } = useSchemas(selectedOrgSlug);
  const [searchText, setSearchText] = useState('');

  // Get layout contexts
  const { setBreadcrumbs, setActionButtons, setOnRefresh, setRefreshLoading } = useTopHeader();

  const handleNewSchema = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      void history({ to: `/orgs/${selectedOrgSlug}/schemas/_new` });
    },
    [history, selectedOrgSlug],
  );

  const handleRowClick = (record: Schema) => {
    void history({ to: `/orgs/${selectedOrgSlug}/schemas/${record.slug}` });
  };

  // Set up header with breadcrumbs and action buttons
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Schemas' },
    ]);
    setActionButtons([
      {
        onClick: handleNewSchema,
        icon: <PlusCircleOutlined />,
        label: 'Add Schema',
        disabled: !access,
      },
    ]);
    setOnRefresh(refetch);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
      setOnRefresh(undefined);
    };
  }, [setBreadcrumbs, setActionButtons, setOnRefresh, selectedOrgSlug, selectedOrganization, access, handleNewSchema, refetch]);

  // Update refresh loading state
  useEffect(() => {
    setRefreshLoading(isRefetching);
  }, [isRefetching, setRefreshLoading]);

  // Filter schemas based on search text
  const filteredSchemas = schemas.filter((schema) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    return (
      schema.name?.toLowerCase().includes(lowerSearch) ||
      schema.slug?.toLowerCase().includes(lowerSearch) ||
      schema.id?.toLowerCase().includes(lowerSearch)
    );
  });

  const columns: ColumnsType<Schema> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text: string, record: Schema) => (
        <a onClick={() => handleRowClick(record)} style={{ cursor: 'pointer' }}>
          {text}
        </a>
      ),
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      sorter: (a, b) => dayjs(a.createdOn).unix() - dayjs(b.createdOn).unix(),
      render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Published',
      dataIndex: 'published',
      key: 'published',
      width: 120,
      align: 'center',
      filters: [
        { text: 'Published', value: true },
        { text: 'Not Published', value: false },
      ],
      onFilter: (value, record) => record.published === value,
      render: (published: boolean) => (published ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} /> : null),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Input
        placeholder="Search schemas..."
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ width: 300 }}
        allowClear
      />
      <Table
        columns={columns}
        dataSource={filteredSchemas}
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
    </Space>
  );
}

export default OrganizationSchemas;
